import { PageHeader } from '@ui-components/neo.tax';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';

import { LockHeader } from '../lock-header';
import { PageContent } from '../organisms.styled';
import { ContentContainer } from './page-layout.styled';
import { PageLockProvider } from './page-lock.context';

export interface PageLayoutProps {
  children: ReactNode;
  subtitle?: ReactNode;
  title: ReactNode;
  alert?: ReactNode;
  trailingContent?: ReactNode;
  pageHeaderBorder?: boolean;
  locked?: boolean;
  lockedReason?: string;
  lockButtons?: ReactNode;
  fullWidth?: boolean;
  /**
   * Which part of the page a lock closes. `page` — the default — closes everything below the header.
   *
   * `regions` hands that job to the caller, for a page where the lock does not cover everything on
   * it: the header still says the page is locked, but nothing is made inert here and the page marks
   * its own locked parts with `LockedRegion`. `inert` is inherited and cannot be undone on a
   * descendant, so a page that needs one control to stay usable in place has to lock around it
   * rather than lock everything.
   */
  lockScope?: 'page' | 'regions';
}

export const PageLayout = ({
  alert,
  children,
  fullWidth,
  lockButtons,
  lockScope = 'page',
  locked,
  lockedReason,
  pageHeaderBorder = true,
  subtitle,
  title,
  trailingContent,
}: PageLayoutProps): ReactElement => {
  const contentRef = useRef<HTMLDivElement>(null);
  const locksWholePage = Boolean(locked) && lockScope === 'page';

  // `pointer-events: none` alone turns a locked page away from a mouse and nothing else: its
  // controls keep their place in the tab order, and any dialog they open is a portal rendered
  // outside this subtree, so it works normally once open. `inert` is what actually closes the page.
  //
  // Set through a ref because React 18 does not recognise `inert` as a DOM prop.
  useEffect((): void => {
    contentRef.current?.toggleAttribute('inert', locksWholePage);
  }, [locksWholePage]);

  return (
    <PageLockProvider value={Boolean(locked)}>
      <PageContent direction="column" gap={6}>
        <LockHeader locked={locked} reason={lockedReason}>
          {lockButtons}
        </LockHeader>
        <ContentContainer ref={contentRef} $locked={locksWholePage} direction="column" fullWidth={fullWidth} gap={6}>
          <PageHeader subtitle={subtitle} title={title} trailingContent={trailingContent} border={pageHeaderBorder} />
          {alert}
          {children}
        </ContentContainer>
      </PageContent>
    </PageLockProvider>
  );
};
